import classNames from "classnames";

import { clampMatchPosition, FilterMatch } from "../Sidebar/filterResult";

import styles from "./Label.module.css";

interface LabelProps {
  text: string;
  title?: string;
  /**
   * Optional match span to highlight within the label, e.g. from a sidebar
   * filter. Positions are interpreted against the full `text` prop (not the
   * visible/truncated rendering).
   *
   * Truncation is position-aware: when a match falls inside the otherwise-
   * ellipsized middle of a long label, the start half is replaced by a windowed
   * snippet (`{prefix}…{context}{HIGHLIGHT}{context}…`) so the match stays
   * visible. The trailing chars (last `END_LENGTH`) are always shown.
   */
  match?: FilterMatch;
}

/** Cutoff character length for middle truncation. */
const END_LENGTH = 13;

/** First chars of the start half kept visible to identify the file by prefix. */
const PREFIX_CHARS = 4;

/** Chars of context shown on each side of a match within the snippet. */
const CONTEXT_CHARS = 4;

/**
 * Styled container used within Sidebar to render labels (files and topics).
 */
export function Label({ text, title, match }: LabelProps) {
  // Heuristic: If text is shorter than twice the cut off length,
  // no middle truncation needed.
  if (text.length <= END_LENGTH * 2) {
    return (
      <div
        className={classNames(styles.container, styles.labelText)}
        title={title ?? text}
      >
        {renderMatch(text, match)}
      </div>
    );
  }

  const start = text.slice(0, -END_LENGTH);
  const end = text.slice(-END_LENGTH);
  const splitIndex = start.length;
  const { startMatch, endMatch } = splitMatch(match, splitIndex, text.length);

  return (
    <div
      className={classNames(styles.middleTruncate, styles.labelText)}
      title={title ?? text}
    >
      <span className={styles.start}>
        {startMatch ? renderStartSnippet(start, startMatch) : start}
      </span>
      <span className={styles.end}>{renderMatch(end, endMatch)}</span>
    </div>
  );
}

function renderMatch(text: string, match?: FilterMatch) {
  if (!match) {
    return text;
  }
  const [from, to] = clampMatchPosition(match.position, text.length);
  // Empty span post-clamping (e.g., inverted input) carries no highlight.
  if (from === to) {
    return text;
  }
  return (
    <>
      {text.slice(0, from)}
      <span className={styles.highlight}>{text.slice(from, to)}</span>
      {text.slice(to)}
    </>
  );
}

/**
 * Render the start half with a windowed snippet around the match so the
 * highlight survives the middle-truncation. The result is always:
 *
 *   {prefix}{optional `…`}{before-context}{HIGHLIGHT}{after-context}{optional `…`}
 *
 * If the match overlaps the prefix region, the prefix renders with the
 * highlight embedded inline (no separate snippet, no leading ellipsis).
 */
function renderStartSnippet(start: string, match: FilterMatch) {
  const [from, to] = match.position;
  const prefixEnd = Math.min(PREFIX_CHARS, start.length);

  // Match starts inside the prefix region: render prefix with embedded highlight.
  // Cap the highlight at a snippet boundary so we don't render the entire start.
  if (from < prefixEnd) {
    const snippetEnd = Math.min(start.length, to + CONTEXT_CHARS);
    const highlightEnd = Math.min(to, snippetEnd);
    return (
      <>
        {start.slice(0, from)}
        <span className={styles.highlight}>
          {start.slice(from, highlightEnd)}
        </span>
        {start.slice(highlightEnd, snippetEnd)}
        {snippetEnd < start.length && "…"}
      </>
    );
  }

  // Match is past the prefix: prefix + optional gap + windowed snippet.
  const snippetStart = Math.max(prefixEnd, from - CONTEXT_CHARS);
  const snippetEnd = Math.min(start.length, to + CONTEXT_CHARS);
  const hasGap = snippetStart > prefixEnd;
  const hasTail = snippetEnd < start.length;

  return (
    <>
      {start.slice(0, prefixEnd)}
      {hasGap && "…"}
      {start.slice(snippetStart, from)}
      <span className={styles.highlight}>{start.slice(from, to)}</span>
      {start.slice(to, snippetEnd)}
      {hasTail && "…"}
    </>
  );
}

/**
 * Distribute a single match span across the start/end halves of a
 * middle-truncated label. A span that lies entirely on one side stays on
 * that side; a span straddling the split is rendered as two pieces.
 */
function splitMatch(
  match: FilterMatch | undefined,
  splitIndex: number,
  totalLength: number,
): { startMatch?: FilterMatch; endMatch?: FilterMatch } {
  if (!match) {
    return {};
  }
  // Clamp at the split layer too so positions feeding into either half are
  // always valid for that half's slice domain.
  const [from, to] = clampMatchPosition(match.position, totalLength);
  if (to === from) {
    return {};
  }
  if (to <= splitIndex) {
    return { startMatch: { position: [from, to] } };
  }
  if (from >= splitIndex) {
    return {
      endMatch: { position: [from - splitIndex, to - splitIndex] },
    };
  }
  return {
    startMatch: { position: [from, splitIndex] },
    endMatch: { position: [0, to - splitIndex] },
  };
}
